'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { useTickerPopup } from './TickerPopupProvider';
import { tickerHref, rememberTickerType } from '@/lib/tickerTypeCache';

export interface TickerLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  ticker: string;
  /** Asset type hint — when the caller already knows the type (search
   *  results, watchlist quotes that carry it, etc.), pass it through so
   *  the rendered href points to the canonical URL on first paint. When
   *  omitted, falls back to the module-level type cache; when the cache
   *  is also empty, defaults to /stocks/ and lets the middleware /
   *  page-level redirect catch any ETFs that slipped through. */
  type?: 'stock' | 'etf';
  /** Suppress the popup and just navigate (for places where popup behavior
   *  isn't desired, e.g. inside the popup itself, or compact lists where a
   *  modifier-key tip isn't useful). Defaults to false. */
  navigateOnly?: boolean;
  /** Honored only by `<Link>` — passed straight through. */
  prefetch?: boolean;
  /** Render as `<button>` instead of `<a>` — required when this link is
   *  visually nested inside an outer `<a>` (e.g. news article cards),
   *  since nested `<a>` is invalid HTML and triggers a React hydration
   *  mismatch. In button mode, click only opens the popup; modifier-key
   *  open-in-new-tab is unsupported (use a non-nested context for that). */
  asButton?: boolean;
  children?: React.ReactNode;
}

/** Drop-in replacement for `<Link href="/stocks/{TICKER}">` that opens the
 *  shared TickerPopup on a regular click while preserving open-in-new-tab
 *  behavior on Cmd/Ctrl/Shift/middle-click. ETF tickers route to
 *  /etf/{TICKER} when we know the type, so users never see the wrong
 *  detail page even on first paint. Falls back to plain navigation when
 *  used outside a TickerPopupProvider. */
export default function TickerLink({
  ticker, type, navigateOnly, prefetch = false, asButton, onClick, children, ...rest
}: TickerLinkProps) {
  const popup = useTickerPopup();

  // Seed the cache opportunistically when a caller provides a type hint —
  // a passing fix benefits future link renders for the same ticker even
  // if they don't all pass the hint. useEffect (not render-time) so React
  // Strict Mode's double-render doesn't write the same entry twice and
  // the side effect doesn't run during render.
  useEffect(() => {
    if (type) rememberTickerType(ticker, type);
  }, [ticker, type]);

  const href = tickerHref(ticker, type);

  // Button mode — used when this link is rendered inside an outer `<a>`
  // (e.g. news article cards). Nested anchors are invalid HTML and
  // produce a React hydration mismatch. Click opens the popup;
  // stopPropagation prevents the outer link from firing on the same
  // event. No modifier-key new-tab handling here — that path doesn't
  // make sense inside a clickable parent anyway.
  if (asButton) {
    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(e as unknown as React.MouseEvent<HTMLAnchorElement>);
      if (e.defaultPrevented) return;
      if (popup) {
        popup.open(ticker, e.currentTarget.getBoundingClientRect());
      } else {
        // Fallback: no popup provider available — navigate in-tab so
        // the ticker chip still does something useful.
        window.location.href = href;
      }
    };
    // Strip anchor-only attributes that don't apply to <button>. The rest
    // (className, style, title, id, tabIndex, role, aria-*) are shared.
    const {
      href: _href, target: _target, rel: _rel, download: _download,
      hrefLang: _hrefLang, ping: _ping, referrerPolicy: _referrerPolicy,
      ...buttonRest
    } = rest;
    void _href; void _target; void _rel; void _download; void _hrefLang;
    void _ping; void _referrerPolicy;
    return (
      <button
        type="button"
        onClick={handleButtonClick}
        data-ticker-link={ticker}
        {...(buttonRest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {children}
      </button>
    );
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Caller can preventDefault to suppress both navigation AND popup
    // (used while a drag is in progress, etc.).
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (navigateOnly || !popup) return;
    // Honor the user's intent to open in a new tab/window.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    popup.open(ticker, e.currentTarget.getBoundingClientRect());
  };

  return (
    <Link
      href={href}
      prefetch={prefetch}
      onClick={handleClick}
      // data-ticker-link tells TickerPopup's outside-click handler to
      // skip closing when another TickerLink is clicked. Without this,
      // clicking a different ticker would close the open popup AND open
      // a new one, causing remount + position recomputation.
      data-ticker-link={ticker}
      {...rest}
    >
      {children}
    </Link>
  );
}
